import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import SiteFooter from "@/components/SiteFooter";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { verifyEmail } = useAuth();
  const [state, setState] = useState("verifying"); // verifying | success | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setState("error");
      setErrorMsg("No verification token provided.");
      return;
    }
    verifyEmail(token)
      .then((u) => {
        setState("success");
        setTimeout(() => navigate(u.onboarded ? "/dashboard" : "/onboarding", { replace: true }), 1200);
      })
      .catch((err) => {
        setState("error");
        setErrorMsg(err?.response?.data?.detail || "Verification failed.");
      });
  }, [params, navigate, verifyEmail]);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-foreground/30 glass-strong">
        <div className="max-w-[1600px] mx-auto px-5 md:px-12 h-14 md:h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <ThemeToggle />
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center px-5 sm:px-6 py-10 sm:py-12">
        <div className="w-full max-w-md panel-glass p-6 sm:p-8 md:p-12 relative corner-marks" data-testid={`verify-${state}`}>
          {state === "verifying" && (
            <>
              <div className="terminal-label mb-3">[ VERIFY.EMAIL ]</div>
              <Loader2 className="w-10 h-10 animate-spin text-foreground/70 mb-6" strokeWidth={1.25} />
              <h1 className="serif-mod text-3xl tracking-tight mb-2" style={{ fontWeight: 500 }}>Verifying…</h1>
              <p className="text-sm text-foreground/65">Hang tight.</p>
            </>
          )}
          {state === "success" && (
            <>
              <div className="terminal-label mb-3 text-primary">[ VERIFIED ]</div>
              <CheckCircle2 className="w-10 h-10 text-primary mb-6" strokeWidth={1.25} />
              <h1 className="serif-mod text-3xl tracking-tight mb-2" style={{ fontWeight: 500 }}>Email verified.</h1>
              <p className="text-sm text-foreground/65">Logging you in…</p>
            </>
          )}
          {state === "error" && (
            <>
              <div className="terminal-label mb-3 text-primary">[ VERIFY.FAILED ]</div>
              <AlertTriangle className="w-10 h-10 text-primary mb-6" strokeWidth={1.25} />
              <h1 className="serif-mod text-3xl tracking-tight mb-3" style={{ fontWeight: 500 }}>Couldn&apos;t verify.</h1>
              <p className="text-sm text-foreground/75 mb-6">{errorMsg}</p>
              <Link to="/auth" className="btn-primary inline-flex items-center" data-testid="verify-back-to-auth">
                Back to log in
              </Link>
            </>
          )}
        </div>
      </div>
      <SiteFooter variant="site" />
    </div>
  );
}
